import type { Round, Tournament } from '../schemas/entities';
import { DEFAULT_FORMAT, formatById } from '../formats';

export interface TournamentSummary {
  tournament: Tournament;
  rounds: number;
  submitted: number;
  /** Rounds started but not submitted. */
  open: number;
  /** Last change to the tournament or any of its rounds (ms). */
  lastActivity: number;
}

export interface Dashboard {
  current: TournamentSummary[];
  upcoming: TournamentSummary[];
  previous: TournamentSummary[];
}

/** A local calendar day as YYYY-MM-DD (the dates judges type; not UTC). */
export const localDay = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const summarizeTournament = (tournament: Tournament, rounds: Round[]): TournamentSummary => ({
  tournament,
  rounds: rounds.length,
  submitted: rounds.filter((r) => r.status === 'submitted').length,
  open: rounds.filter((r) => r.status === 'inRound' || r.status === 'deciding' || r.status === 'committed').length,
  lastActivity: Math.max(tournament.updatedAt, ...rounds.map((r) => r.updatedAt)),
});

/**
 * Sorts tournaments for the dashboard.
 * - Current: today is within its dates; or, undated, it was touched today or has a round still open.
 * - Upcoming: starts after today (soonest first).
 * - Previous: everything else (most recent first).
 */
export const dashboard = (items: TournamentSummary[], now: number): Dashboard => {
  const today = localDay(now);
  const out: Dashboard = { current: [], upcoming: [], previous: [] };
  for (const s of items) {
    const { startDate, endDate } = s.tournament;
    const end = endDate ?? startDate;
    if (startDate && startDate > today) out.upcoming.push(s);
    else if (startDate && end! >= today) out.current.push(s);
    else if (!startDate && (localDay(s.lastActivity) === today || s.open > 0)) out.current.push(s);
    else out.previous.push(s);
  }
  const recent = (a: TournamentSummary, b: TournamentSummary) => b.lastActivity - a.lastActivity;
  out.current.sort(recent);
  out.upcoming.sort((a, b) => a.tournament.startDate!.localeCompare(b.tournament.startDate!));
  const ended = (s: TournamentSummary) => s.tournament.endDate ?? s.tournament.startDate ?? localDay(s.lastActivity);
  out.previous.sort((a, b) => ended(b).localeCompare(ended(a)) || recent(a, b));
  return out;
};

/** "Oct 3", "Oct 3–4", "Oct 30 – Nov 1" (year only when it isn't this year). */
export const dateRange = (startDate: string | undefined, endDate: string | undefined, now: number): string | null => {
  if (!startDate) return null;
  const parse = (d: string) => new Date(`${d}T12:00:00`);
  const thisYear = new Date(now).getFullYear();
  const a = parse(startDate);
  const b = parse(endDate ?? startDate);
  const withYear = b.getFullYear() !== thisYear;
  const md = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const y = withYear ? `, ${b.getFullYear()}` : '';
  if (!endDate || endDate === startDate) return `${md(a)}${y}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return `${md(a)}–${b.getDate()}${y}`;
  return `${md(a)} – ${md(b)}${y}`;
};

/**
 * The event a new round starts with: the latest round's in this tournament (a pool is usually one event), else the
 * judge's latest round anywhere, else the tournament's old default (from before events were per round), else PF.
 */
export const nextRoundFormatId = (tournamentRounds: Round[], latestAnywhere?: Round | null, tournamentFormatId?: string): string => {
  const latest = [...tournamentRounds].sort((a, b) => b.createdAt - a.createdAt)[0] ?? latestAnywhere ?? null;
  const id = latest?.format.id ?? tournamentFormatId;
  return formatById(id) ? id! : DEFAULT_FORMAT.id;
};

/** The events judged at a tournament, in the order first judged ("Public Forum (CHSSA)", "Lincoln-Douglas (CHSSA)"). */
export const eventsJudged = (rounds: Round[]): string[] =>
  [...new Set([...rounds].sort((a, b) => a.createdAt - b.createdAt).map((r) => r.format.name))];
